import React, { useEffect, useRef } from 'react';
import BaseLayout from '../../components/layout/BaseLayout';
import styles from './ContactPage.module.css';
import { MdKeyboardArrowRight, MdOutlineCall } from 'react-icons/md';
import { FaTerminal } from 'react-icons/fa';
import Button from '../../components/Button/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import SEOMetaTags from '../../components/SEO/SEOMetaTags';

import questionIcon from '../../assets/images/question-icon.svg';
import HeroSectionStatic from '../../components/sections/Hero/HeroSectionStatic';
import { isSmallScreen } from '../../utils/screen';
import { useTranslation } from '../../hooks/useTranslation';
import Accordion, { AccordionItemProps } from '../../components/Accordion/Accordion';

const ContactPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const calendarRef = useRef<HTMLDivElement>(null);
    const { t, tJsx } = useTranslation();

    // FAQ questions with translation keys - moved inside component to access t()
    const FAQ_QUESTIONS = [
        {
            id: 1,
            text: t("contact.faq.question1")
        },
        {
            id: 2,
            text: t("contact.faq.question2")
        },
        {
            id: 3,
            text: t("contact.faq.question3")
        },
        {
            id: 4,
            text: t("contact.faq.question4")
        },
        {
            id: 5,
            text: t("contact.faq.question5")
        },
        {
            id: 6,
            text: t("contact.faq.question6")
        },
        {
            id: 7,
            text: t("contact.faq.question7")
        },
        {
            id: 8,
            text: t("contact.faq.question8")
        },
        {
            id: 9,
            text: t("contact.faq.question9")
        },
        {
            id: 10,
            text: t("contact.faq.question10")
        },
        {
            id: 11,
            text: t("contact.faq.question11")
        },
        {
            id: 12,
            text: t("contact.faq.question12")
        },
        {
            id: 13,
            text: t("contact.faq.question13")
        },
    ];

    // Create FAQ accordion items
    const faqAccordionItems: AccordionItemProps[] = FAQ_QUESTIONS.map(question => ({
        id: question.id,
        title: question.text,
        content: tJsx(`contact.faq.answer${question.id}`),
    }));

    // Scroll to calendar section if state parameter is set
    useEffect(() => {
        const state = location.state as { scrollToCalendar?: boolean } | null;
        if (state?.scrollToCalendar && calendarRef.current) {
            const elementPosition = calendarRef.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 64;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    }, [location.state]);

    return (
        <>
            <SEOMetaTags
                title={{
                    pl: "Kontakt - Appotype | Umów Spotkanie, Konsultacja IT, Wycena Projektu",
                    en: "Contact - Appotype | Book Meeting, IT Consultation, Project Quote"
                }}
                description={{
                    pl: "Skontaktuj się z Appotype (Software Agency, Warszawa). Zadzwoń, umów spotkanie online lub uzyskaj wycenę projektu IT za pomocą naszego kalkulatora. Porozmawiajmy!",
                    en: "Contact Appotype (Software Agency, Warsaw). Call us, book an online meeting, or get an IT project quote using our calculator. Let's talk!"
                }}
                canonicalUrl="/contact"
            />
            <BaseLayout>
                <HeroSectionStatic
                    title={t("contact.hero.title")}
                    subtitle={t("contact.hero.subtitle")}
                    heroButtons={[
                        {
                            label: t("contact.hero.button2"),
                            variant: "secondary",
                            rightIcon: <MdOutlineCall size={24} />,
                            onClick: () => (window as any).toggleDialog()
                        },
                        {
                            label: t("contact.hero.button1"),
                            variant: "outlineNoTheme",
                            rightIcon: <MdKeyboardArrowRight size={24} />,
                            onClick: () => {
                                if (calendarRef.current) {
                                    const elementPosition = calendarRef.current.getBoundingClientRect().top;
                                    const offsetPosition = elementPosition + window.pageYOffset - 64;
                                    window.scrollTo({
                                        top: offsetPosition,
                                        behavior: 'smooth'
                                    });
                                }
                            }
                        }
                    ]}
                />

                <section className={styles.calendar} ref={calendarRef}>
                    <div className={styles.calendarContainer}>
                        <div className={styles.calendarHeader}>
                            <h2 className={styles.calendarTitle}>
                                {t("contact.calendar.title")}
                            </h2>
                        </div>
                        <div className={styles.calendarWrapper}>
                            <iframe
                                src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ0TILotoukEuCSWq0fQLZcIOt3yPmNTSPD2I16SVrjzpLYy5ai7Jp9C_FpHNSCST4lCWSShGy6l?gv=true"
                                style={{ border: 0 }}
                                width="100%"
                                height="871"
                            ></iframe>
                        </div>
                    </div>
                </section>

                <section id="calculatorSection">
                    <div className={styles.calculatorContainer}>
                        <div className={styles.leftColumn}>
                            <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                            <div className={styles.leftColumnContainer}>
                                <h2 className={styles.calculatorTitle}>
                                    {t("contact.calculator.title")}
                                </h2>
                                <p className={styles.calculatorDescription}>
                                    {t("contact.calculator.description")}
                                </p>
                                <Button
                                    variant="primary"
                                    className={styles.calculatorButton}
                                    rightIcon={<MdKeyboardArrowRight size={24} />}
                                    onClick={() => navigate('/calculator')}
                                >
                                    {t("contact.calculator.button")}
                                </Button>
                            </div>
                        </div>
                        <div className={styles.rightColumn}>
                            <div className={styles.steps}>
                                <div className={styles.step}>
                                    <div className={styles.stepNumber}>
                                        {t("contact.calculator.step1")}
                                    </div>
                                    <div className={styles.stepContent}>
                                        {t("contact.calculator.step1.description")}
                                    </div>
                                </div>
                                <div className={styles.stepsArrow}>
                                    <MdKeyboardArrowRight size={32} />
                                </div>
                                <div className={styles.step}>
                                    <div className={styles.stepNumber}>
                                        {t("contact.calculator.step2")}
                                    </div>
                                    <div className={styles.stepContent}>
                                        {t("contact.calculator.step2.description")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className={styles.faq}>
                    <div className={styles.faqContainer}>
                        <div className={styles.faqIcon}>
                            <FaTerminal size={isSmallScreen() ? 44 : 54} />
                        </div>
                        <div className={styles.faqContent}>
                            <div className={styles.faqHeaderContent}>
                                <h2 className={styles.faqTitle}>
                                    {t("contact.faq.title")}
                                </h2>
                                <p className={styles.faqDescription}>
                                    {t("contact.faq.description")}
                                </p>
                            </div>
                            <div className={styles.accordionContainer}>
                                <Accordion 
                                    items={faqAccordionItems} 
                                    allowMultiple={false}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </BaseLayout>
        </>
    );
};

export default ContactPage;